import React, { useState, useEffect } from 'react'
import ApiUsers from '../../api/ApiUsers'
import ApiPlayers from '../../api/ApiPlayers'
import toastr from 'toastr'
import './InlineProfileCompletionModal.css'

function computeInitialFormData(show, primaryPlayer, user) {
  const safeShow = Boolean(show && primaryPlayer && user)
  const skill_level = safeShow
    ? primaryPlayer.skill_level !== null &&
      primaryPlayer.skill_level !== undefined &&
      primaryPlayer.skill_level !== ''
      ? String(parseFloat(primaryPlayer.skill_level).toFixed(1))
      : ''
    : ''
  return {
    firstname: safeShow ? user.firstname || '' : '',
    lastname: safeShow ? user.lastname || '' : '',
    skill_level,
    gender: safeShow ? primaryPlayer.gender || '' : '',
  }
}

export default function InlineProfileCompletionModal({
  show,
  onComplete,
  onSkip,
  primaryPlayer,
  user,
  allowSkip = true,
  requireSkillLevel = true,
}) {
  const [formData, setFormData] = useState(() =>
    computeInitialFormData(show, primaryPlayer, user),
  )
  const [saving, setSaving] = useState(false)
  const [errors, setErrors] = useState({})

  // Initialize form data when modal opens
  useEffect(() => {
    if (show && primaryPlayer && user) {
      setFormData(computeInitialFormData(true, primaryPlayer, user))
    }
  }, [show, primaryPlayer, user])

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }))
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }))
    }
  }

  const validateForm = () => {
    const newErrors = {}

    if (!formData.firstname.trim()) {
      newErrors.firstname = 'First name is required'
    }
    if (!formData.lastname.trim()) {
      newErrors.lastname = 'Last name is required'
    }
    if (requireSkillLevel && !formData.skill_level) {
      newErrors.skill_level = 'Skill level is required'
    }

    if (formData.skill_level) {
      if (
        isNaN(formData.skill_level) ||
        formData.skill_level < 1 ||
        formData.skill_level > 7
      ) {
        newErrors.skill_level = 'Skill level must be between 1.0 and 7.0'
      }
    }
    if (!formData.gender) {
      newErrors.gender = 'League category is required'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSave = async () => {
    if (!validateForm()) return

    setSaving(true)
    try {
      // Update user data
      await ApiUsers.update({
        ...user,
        firstname: formData.firstname,
        lastname: formData.lastname,
      })

      // Update player data
      const playerUpdate = {
        id: primaryPlayer.id,
        first_name: formData.firstname,
        last_name: formData.lastname,
        gender: formData.gender,
      }

      // Only include skill_level if it has a value, or clear it if not required
      if (formData.skill_level) {
        playerUpdate.skill_level = parseFloat(formData.skill_level)
      } else if (!requireSkillLevel) {
        playerUpdate.skill_level = null
      }

      await ApiPlayers.update(playerUpdate)

      toastr.success('Profile completed successfully!')
      onComplete()
    } catch (error) {
      console.error('Error saving profile:', error)
      toastr.error(
        'Error saving profile: ' + (error.message || 'Please try again'),
      )
    } finally {
      setSaving(false)
    }
  }

  const handleSkip = () => {
    onSkip()
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Escape' && onSkip && allowSkip) {
      handleSkip()
    }
  }

  if (!show) return null

  return (
    <div
      className="fl-sheet-overlay"
      tabIndex="-1"
      role="dialog"
      aria-modal="true"
      aria-labelledby="profile-completion-title"
      onKeyDown={handleKeyDown}
    >
      <div className="fl-sheet" role="document">
        <div className="fl-sheet-header">
          <h2 id="profile-completion-title" className="fl-sheet-title">
            Complete Your Profile
          </h2>
          <p className="fl-sheet-subtitle">
            Just a few details to get you playing.
          </p>
        </div>

        <div className="fl-sheet-body">
          {/* Sections: 16px gaps */}
          <form className="fl-sections">
            {/* Row group: 12px gap, 2 columns on desktop */}
            <div className="fl-row">
              <div className="fl-field">
                <label htmlFor="firstname" className="fl-label">
                  First Name <span className="fl-required">*</span>
                </label>
                <input
                  id="firstname"
                  name="firstname"
                  type="text"
                  className={`fl-input ${errors.firstname ? 'fl-invalid' : ''}`}
                  value={formData.firstname}
                  onChange={handleInputChange}
                  placeholder="Your first name"
                />
                {errors.firstname && (
                  <div className="fl-error">{errors.firstname}</div>
                )}
              </div>

              <div className="fl-field">
                <label htmlFor="lastname" className="fl-label">
                  Last Name <span className="fl-required">*</span>
                </label>
                <input
                  id="lastname"
                  name="lastname"
                  type="text"
                  className={`fl-input ${errors.lastname ? 'fl-invalid' : ''}`}
                  value={formData.lastname}
                  onChange={handleInputChange}
                  placeholder="Your last name"
                />
                {errors.lastname && (
                  <div className="fl-error">{errors.lastname}</div>
                )}
              </div>
            </div>

            <div className="fl-row">
              {requireSkillLevel && (
                <div className="fl-field">
                  <label htmlFor="skill_level" className="fl-label">
                    Tennis Skill Level <span className="fl-required">*</span>
                  </label>
                  <select
                    id="skill_level"
                    name="skill_level"
                    className={`fl-input ${errors.skill_level ? 'fl-invalid' : ''}`}
                    value={formData.skill_level}
                    onChange={handleInputChange}
                  >
                    <option value="">Select your skill level</option>
                    <option value="1.0">1.0 - Beginner</option>
                    <option value="1.5">1.5 - Beginner+</option>
                    <option value="2.0">2.0 - High Beginner</option>
                    <option value="2.5">2.5 - High Beginner+</option>
                    <option value="3.0">3.0 - Intermediate</option>
                    <option value="3.5">3.5 - Intermediate+</option>
                    <option value="4.0">4.0 - Advanced</option>
                    <option value="4.5">4.5 - Advanced+</option>
                    <option value="5.0">5.0 - Expert</option>
                    <option value="5.5">5.5 - Expert+</option>
                    <option value="6.0">6.0 - Professional</option>
                    <option value="6.5">6.5 - Professional+</option>
                    <option value="7.0">7.0 - World Class</option>
                  </select>
                  {errors.skill_level && (
                    <div className="fl-error">{errors.skill_level}</div>
                  )}
                </div>
              )}

              <div className="fl-field">
                <label htmlFor="gender" className="fl-label">
                  League Category <span className="fl-required">*</span>
                </label>
                <select
                  id="gender"
                  name="gender"
                  className={`fl-input ${errors.gender ? 'fl-invalid' : ''}`}
                  value={formData.gender}
                  onChange={handleInputChange}
                >
                  <option value="">Select league type</option>
                  <option value="Men">Men's Leagues</option>
                  <option value="Women">Women's Leagues</option>
                </select>
                <small className="fl-help-text">
                  This determines which league divisions you'll be eligible to
                  join
                </small>
                {errors.gender && (
                  <div className="fl-error">{errors.gender}</div>
                )}
              </div>
            </div>
          </form>
        </div>

        <div className="fl-actions">
          {allowSkip && (
            <button
              type="button"
              className="fl-btn fl-btn-secondary"
              onClick={handleSkip}
              disabled={saving}
            >
              Skip for now
            </button>
          )}
          <button
            type="button"
            className="fl-btn fl-btn-primary"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Complete Profile'}
          </button>
        </div>
      </div>
    </div>
  )
}
